/**
 * Client for POST /api/cognitory/v2/platform-admin/systems/:systemId/initial-owner
 */

import { resolveCognitoryApiBaseUrl } from "./cognitoryApiBase";
import { resolveCognitoryBearerToken } from "./cognitoryBearerToken";

export type InitialOwnerSelection = "self" | "other";

export type InitialOwnerAssignmentPlan = {
  systemId: string;
  ownerSelection: InitialOwnerSelection;
  maskedOwnerEmail: string;
  role: "owner";
  status: "active";
  planTimestamp: string;
  writeSetHash: string;
};

export type InitialOwnerPlanResult =
  | {
      kind: "planned";
      requestId: string;
      plan: InitialOwnerAssignmentPlan;
    }
  | {
      kind:
        | "already_assigned"
        | "unauthenticated"
        | "forbidden"
        | "validation_failed"
        | "initial_owner_state_invalid"
        | "initial_owner_conflict"
        | "owner_identity_unverified"
        | "temporary_failure"
        | "unable_to_verify";
      requestId: string | null;
    };

export type InitialOwnerExecuteResult =
  | {
      kind: "assigned" | "already_assigned";
      requestId: string;
      systemId: string;
    }
  | {
      kind:
        | "unauthenticated"
        | "forbidden"
        | "validation_failed"
        | "initial_owner_state_invalid"
        | "initial_owner_conflict"
        | "owner_identity_unverified"
        | "temporary_failure"
        | "unable_to_verify";
      requestId: string | null;
    };

function buildInitialOwnerPath(systemId: string): string {
  return `/api/cognitory/v2/platform-admin/systems/${encodeURIComponent(systemId)}/initial-owner`;
}

async function postInitialOwner(
  systemId: string,
  body: Record<string, unknown>,
  input?: {
    apiBaseUrl?: string;
    bearerToken?: string | null;
    fetchImpl?: typeof fetch;
  },
): Promise<{ status: number; body: unknown; requestId: string | null }> {
  const fetchImpl = input?.fetchImpl ?? fetch;
  const token =
    input?.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { status: 401, body: null, requestId: null };
  }

  const base = input?.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  let response: Response;
  try {
    response = await fetchImpl(`${base}${buildInitialOwnerPath(systemId)}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      cache: "no-store",
      body: JSON.stringify(body),
    });
  } catch {
    return { status: 503, body: null, requestId: null };
  }

  let parsed: unknown = null;
  try {
    parsed = await response.json();
  } catch {
    parsed = null;
  }

  const requestId =
    parsed &&
    typeof parsed === "object" &&
    typeof (parsed as { requestId?: unknown }).requestId === "string"
      ? (parsed as { requestId: string }).requestId
      : null;

  return { status: response.status, body: parsed, requestId };
}

function isPlan(value: unknown): value is InitialOwnerAssignmentPlan {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.systemId === "string" &&
    (record.ownerSelection === "self" || record.ownerSelection === "other") &&
    typeof record.maskedOwnerEmail === "string" &&
    record.role === "owner" &&
    record.status === "active" &&
    typeof record.planTimestamp === "string" &&
    typeof record.writeSetHash === "string"
  );
}

export async function planPlatformAdministratorInitialOwner(input: {
  systemId: string;
  ownerSelection: InitialOwnerSelection;
  ownerEmail?: string;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<InitialOwnerPlanResult> {
  const body: Record<string, unknown> = {
    phase: "plan",
    ownerSelection: input.ownerSelection,
  };
  if (input.ownerSelection === "other") {
    body.ownerEmail = input.ownerEmail;
  }

  const {
    status,
    body: responseBody,
    requestId,
  } = await postInitialOwner(input.systemId, body, input);

  if (status === 401) return { kind: "unauthenticated", requestId };
  if (status === 403) return { kind: "forbidden", requestId };
  if (status === 409) return { kind: "initial_owner_conflict", requestId };
  if (status === 503) return { kind: "temporary_failure", requestId };
  if (status === 400) {
    const decision =
      responseBody &&
      typeof responseBody === "object" &&
      (responseBody as { decision?: unknown }).decision;
    if (decision === "initial_owner_state_invalid") {
      return { kind: "initial_owner_state_invalid", requestId };
    }
    if (decision === "owner_identity_unverified") {
      return { kind: "owner_identity_unverified", requestId };
    }
    return { kind: "validation_failed", requestId };
  }
  if (status !== 200 || !responseBody || typeof responseBody !== "object") {
    return { kind: "unable_to_verify", requestId };
  }

  const decision = (responseBody as { decision?: unknown }).decision;
  if (decision === "initial_owner_already_assigned") {
    return { kind: "already_assigned", requestId };
  }
  if (decision !== "initial_owner_planned") {
    return { kind: "unable_to_verify", requestId };
  }

  const plan = (responseBody as { plan?: unknown }).plan;
  if (!isPlan(plan)) {
    return { kind: "unable_to_verify", requestId };
  }

  return { kind: "planned", requestId: requestId ?? "unknown", plan };
}

export async function executePlatformAdministratorInitialOwner(input: {
  systemId: string;
  ownerSelection: InitialOwnerSelection;
  ownerEmail?: string;
  planTimestamp: string;
  writeSetHash: string;
  confirmAssignInitialOwner: boolean;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<InitialOwnerExecuteResult> {
  const body: Record<string, unknown> = {
    phase: "execute",
    ownerSelection: input.ownerSelection,
    planTimestamp: input.planTimestamp,
    writeSetHash: input.writeSetHash,
    confirmAssignInitialOwner: input.confirmAssignInitialOwner,
  };
  if (input.ownerSelection === "other") {
    body.ownerEmail = input.ownerEmail;
  }

  const {
    status,
    body: responseBody,
    requestId,
  } = await postInitialOwner(input.systemId, body, input);

  if (status === 401) return { kind: "unauthenticated", requestId };
  if (status === 403) return { kind: "forbidden", requestId };
  if (status === 409) return { kind: "initial_owner_conflict", requestId };
  if (status === 503) return { kind: "temporary_failure", requestId };
  if (status === 400) {
    const decision =
      responseBody &&
      typeof responseBody === "object" &&
      (responseBody as { decision?: unknown }).decision;
    if (decision === "initial_owner_state_invalid") {
      return { kind: "initial_owner_state_invalid", requestId };
    }
    if (decision === "owner_identity_unverified") {
      return { kind: "owner_identity_unverified", requestId };
    }
    return { kind: "validation_failed", requestId };
  }
  if (status !== 200 || !responseBody || typeof responseBody !== "object") {
    return { kind: "unable_to_verify", requestId };
  }

  const decision = (responseBody as { decision?: unknown }).decision;
  if (
    decision !== "initial_owner_assigned" &&
    decision !== "initial_owner_already_assigned"
  ) {
    return { kind: "unable_to_verify", requestId };
  }

  const system = (responseBody as { system?: unknown }).system;
  if (
    !system ||
    typeof system !== "object" ||
    typeof (system as { systemId?: unknown }).systemId !== "string"
  ) {
    return { kind: "unable_to_verify", requestId };
  }

  return {
    kind:
      decision === "initial_owner_assigned" ? "assigned" : "already_assigned",
    requestId: requestId ?? "unknown",
    systemId: (system as { systemId: string }).systemId,
  };
}
